import { Box, Stack, styled, Typography } from "@mui/material";
import { type PropsWithChildren, type ReactNode, useEffect } from "react";

import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { Sidebar } from "Shared/Layouts/Sidebar";

type Props = {
  preTitle?: string | ReactNode;
  title?: string;
  subtitle?: string;
  titleStartAdornment?: ReactNode;
  titleEndAdornment?: ReactNode;
  action?: ReactNode;
};

const StyledBox = styled(Box)`
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: ${(props) => props.theme.palette.background.paper};
`;

const StyledMainContent = styled(Box)`
  flex-grow: 1;
  overflow-y: auto;
  padding: ${(props) => props.theme.spacing(10)};
  background: ${(props) => props.theme.palette.background.paper};
`;

const PageHeader = styled(Box)`
  margin-bottom: ${(props) => props.theme.spacing(Spacing.L)};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PageHeaderContent = styled(Box)`
  flex: 1;
`;

export const AuthenticatedLayout = ({
  preTitle,
  title,
  subtitle,
  titleStartAdornment,
  titleEndAdornment,
  action,
  children,
}: PropsWithChildren<Props>): React.ReactNode => {
  const { t } = useResource();

  useEffect(() => {
    const docTitle =
      title || (typeof preTitle === "string" ? preTitle : undefined);
    if (docTitle) {
      document.title = `${docTitle} | ${t(Resources.Common.AppName)}`;
    }
  }, [title, preTitle, t]);

  return (
    <StyledBox>
      <Sidebar />
      <StyledMainContent>
        <PageHeader>
          <Box display="flex">
            <PageHeaderContent>
              {preTitle &&
                (typeof preTitle === "string" ? (
                  <Typography
                    sx={(theme) => ({
                      fontSize: "20px",
                      color: theme.colors.textLight,
                      marginBottom: 1,
                    })}
                  >
                    {preTitle}
                  </Typography>
                ) : (
                  <Box sx={{ marginBottom: 1 }}>{preTitle}</Box>
                ))}
              <Stack direction="row" spacing={4} alignItems="center">
                {titleStartAdornment}
                {title && <Typography variant="h1">{title}</Typography>}
                {titleEndAdornment}
              </Stack>
              {subtitle && (
                <Typography variant="subtitle1">{subtitle}</Typography>
              )}
            </PageHeaderContent>
          </Box>

          {action && <Box mb={1}>{action}</Box>}
        </PageHeader>
        {children}
      </StyledMainContent>
    </StyledBox>
  );
};
